import { Component, effect, forwardRef, input, numberAttribute, output, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NumberDirective } from './form-utils/number.validator';
import { UiModule } from './ui';


const INPUT_SLIDER_VALUE_ACCESSOR_PROVIDER = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => InputSliderComponent),
    multi: true,
};

const clamp = (value: number | null, min: number, max: number): number =>
    value === null ? min : Math.min(max, Math.max(min, value));

@Component({
    selector: 'input-slider',
    imports: [
        FormsModule,
        NumberDirective,
        UiModule,
    ],
    providers: [INPUT_SLIDER_VALUE_ACCESSOR_PROVIDER],
    styles: [`
        :host {
            display: block;
            width: 100%;
        }

        .input-slider-wrapper {
            width: 100%;
            min-width: 0;
        }

        .input-slider-wrapper.disabled .value-input {
            border-color: transparent;
            transition: none;
        }

        .input-slider-header {
            display: flex;
            width: 100%;
            justify-content: space-between;
            align-items: baseline;
            padding-bottom: 6px;
        }

        .input-slider-header ui-checkbox {
            padding-left: 3px;
        }

        .range-hint {
            margin-left: auto;
            white-space: nowrap
        }

        .input-slider {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 12px;
            max-width: 380px;
            min-width: 0;
        }

        .slider {
            width: auto;
            padding: 0 9px;
            box-sizing: border-box;
            max-width: 100%;
            min-width: 180px;
            flex-shrink: 1;
            flex-grow: 1;
        }

        .value-group {
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 0 0 auto;
            white-space: nowrap;
        }

        .value-input {
            width: 60px;
            text-align: center !important;
            transition: none;
        }

        .unit {
            white-space: nowrap;
        }

        @media (max-width: 480px) {
            .input-slider {
                row-gap: 8px;
                padding-right: 0;
            }

            .slider{
                flex: 1 1 100%;
                min-width: 0;
            }
        }
    `],
    template: `
        <div class="input-slider-wrapper" [class.disabled]="isInputDisabled()">
            <div class="input-slider-header">
                <ui-checkbox [ngModel]="filterEnabled()" (ngModelChange)="onFilterEnabledChange($event)" [disabled]="formDisabled()">
                    {{ label() }}
                </ui-checkbox>
                <span class="range-hint text-muted">
                    {{ min() }}–{{ max() }} {{ unit() }}
                </span>
            </div>

            <div class="input-slider">
                <ui-slider
                    class="slider"
                    [min]="min()"
                    [max]="max()"
                    [step]="step()"
                    [ngModel]="value()"
                    (ngModelChange)="onValueChange($event)"
                    [disabled]="isInputDisabled()"
                    (blur)="onTouched()">
                </ui-slider>

                <div class="value-group">
                    <input
                        class="form-control value-input"
                        type="ui-number"
                        [min]="min()"
                        [max]="max()"
                        [step]="step()"
                        [ngModel]="value()"
                        (ngModelChange)="onValueChange($event)"
                        [disabled]="isInputDisabled()"
                        (blur)="onTouched()">
                    <span class="unit" [class.text-muted]="isInputDisabled()">{{ unit() }}</span>
                </div>
            </div>
        </div>
    `,
})
export class InputSliderComponent implements ControlValueAccessor {

    label = input('');

    min = input(0, { transform: numberAttribute });

    max = input(100, { transform: numberAttribute });

    step = input(1, { transform: numberAttribute });

    unit = input('km');

    value = signal(0);

    filterEnabled = input(false);

    filterEnabledChange = output<boolean>();

    formDisabled = signal(false);

    private onChange = (_value: number | null) => {};

    onTouched = () => {};

    constructor() {
        effect(() => {
            const min = this.min();
            const max = this.max();
            const value = this.value();

            const next = clamp(value, min, max);

            if (value !== next) {
                this.value.set(next);
            }
        });
    }

    isInputDisabled(): boolean {
        const disabled = this.formDisabled();
        const filterEnabled = this.filterEnabled();
        return disabled || !filterEnabled;
    }

    onValueChange(value: number | null): void {
        if (!this.filterEnabled()) {
            return;
        }

        const next = clamp(value, this.min(), this.max());

        if (this.value() !== next) {
            this.value.set(next);
            this.onChange(next);
        }
    }

    onFilterEnabledChange(filterEnabled: boolean): void {
        this.filterEnabledChange.emit(filterEnabled);
    }

    writeValue(obj: unknown): void {
        const n = Number(obj);
        const next = Number.isFinite(n)
            ? clamp(n, this.min(), this.max())
            : this.min();

        this.value.set(next);
    }

    registerOnChange(fn: (value: number | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.formDisabled.set(isDisabled);
    }
}
